import {Locale} from '../Locale';
import {DataHelper} from '../../helper/data.helper';

export enum ContestTaskType {
  standard = 'standard',
  advanced = 'advanced',
}

export function localizeContestTaskType(type: ContestTaskType): string {
  let value: string = '';

  switch (type) {
    case ContestTaskType.standard:
      value = $localize`:General|Contest task type standard title@@general-contest-task-type-standard-title:Standard`;
      break;
    case ContestTaskType.advanced:
      value = $localize`:General|Contest task type advanced title@@general-contest-task-type-advanced-title:Advanced`;
      break;
  }

  return value;
}

export function localizeContestTaskDescription(type: ContestTaskType): string {
  let value: string = '';

  switch (type) {
    case ContestTaskType.standard:
      value = $localize`:General|Contest task type standard description@@general-contest-task-type-standard-description:for standard classes`;
      break;
    case ContestTaskType.advanced:
      value = $localize`:General|Contest task type advanced description@@general-contest-task-type-advanced-description:for classes with in-depth study`;
      break;
  }

  return value;
}

export type LocaleSections = Record<Locale | string, Section[]>;
type LocaleSectionsDto = Record<Locale | string, SectionDto[]>;
type IterableItem = Section | Question | Answer;

export const ITEM_INDEX_COMPARATOR = (i1: IterableItem, i2: IterableItem) => !i1 || !i2 ? 0 : (i1.index || 0) - (i2.index || 0);

function updateIndexes(items: Section[] | Question[] | Answer[]) {
  items.forEach((item: IterableItem, index: number) => {
    item.index = index + 1;
  });
}

export class ContestTask {
  public id?: string;
  public type: ContestTaskType;
  public locales: Locale[];

  public sections: LocaleSections;

  constructor(type: ContestTaskType) {
    this.type = type;
    this.locales = [];
    this.sections = {};
  }

  public static parse(dto: ContestTaskDto): ContestTask {
    const task = new ContestTask(dto.type);

    task.id = dto.id;
    task.locales = dto.locales || [];
    task.locales.sort();

    task.sections = {};
    Object.keys(dto.sections).forEach(locale => {
      task.sections[locale] = dto.sections[locale].map(sectionDto => Section.parse(sectionDto));
      task.sections[locale].sort(ITEM_INDEX_COMPARATOR);
    });

    return task;
  }

  public clone(preserveId = false): ContestTask {
    const task = new ContestTask(this.type);

    if (preserveId) task.id = this.id;
    task.locales = [];
    this.locales.forEach(locale => task.locales.push(locale));

    task.sections = {};
    Object.keys(this.sections).forEach(locale => {
      task.sections[locale] = this.sections[locale].map(section => section.clone(preserveId));
    });

    return task;
  }

  public prepareForSave(): void {
    if (DataHelper.isAutoGeneratedId(this.id)) this.id = undefined;

    Object.keys(this.sections).forEach(locale => {
      this.sections[locale].forEach(section => section.prepareForSave());

      this.sections[locale].sort(ITEM_INDEX_COMPARATOR);
      updateIndexes(this.sections[locale]);
    });
  }
}

export interface ContestTaskDto {
  id: string;
  type: ContestTaskType;
  locales: Locale[];
  sections: LocaleSectionsDto;
}

export class Section {
  public id?: string;
  public name?: string;
  public index?: number;
  public questions: Question[];

  constructor() {
    this.id = DataHelper.generateId();
    this.questions = [];
  }

  public static parse(dto: SectionDto): Section {
    const section = new Section();

    section.id = dto.id;
    section.name = dto.name;
    section.index = dto.index;
    section.questions = dto.questions.map(questionDto => Question.parse(questionDto));
    section.questions.sort(ITEM_INDEX_COMPARATOR);

    return section;
  }

  public clone(preserveId = false): Section {
    const section = new Section();

    if (preserveId) section.id = this.id;
    section.name = this.name;
    section.index = this.index;
    section.questions = this.questions.map(question => question.clone(preserveId));

    return section;
  }

  public prepareForSave(): void {
    if (DataHelper.isAutoGeneratedId(this.id)) this.id = undefined;
    this.questions.forEach(question => question.prepareForSave());
    this.questions.sort(ITEM_INDEX_COMPARATOR);
    updateIndexes(this.questions);
  }
}

export interface SectionDto {
  id?: string;
  name?: string;
  index?: number;
  questions: QuestionDto[];
}

export enum QuestionType {
  single_choice = 'single_choice',
  multiple_choice = 'multiple_choice',
}

export function localizeQuestionType(type: QuestionType): string {
  let value: string = '';

  switch (type) {
    case QuestionType.single_choice:
      value = $localize`:General|Contest question type single_choice title@@general-contest-question-type-single_choice-title:Single choice`;
      break;
    case QuestionType.multiple_choice:
      value = $localize`:General|Contest question type multiple_choice title@@general-contest-question-type-multiple_choice-title:Multiple choice`;
      break;
  }

  return value;
}

export class Question {
  public id?: string;
  public type!: QuestionType;
  public text?: string;
  public image?: string;
  public audio?: string;
  public video?: string;
  public index?: number;
  public points?: number;
  public answers: Answer[];

  public answerInProgress = false;  // needed only for UI

  constructor() {
    this.id = DataHelper.generateId();
    this.answers = [];
  }

  public static parse(dto: QuestionDto): Question {
    const question = new Question();

    question.id = dto.id;
    question.type = dto.type;
    question.text = dto.text;
    question.image = dto.image;
    question.audio = dto.audio;
    question.video = dto.video;
    question.index = dto.index;
    question.points = dto.points || 1;
    question.answers = dto.answers.map(answerDto => Answer.parse(answerDto));
    question.answers.sort(ITEM_INDEX_COMPARATOR);

    return question;
  }

  public clone(preserveId = false): Question {
    const question = new Question();

    if (preserveId) question.id = this.id;
    question.type = this.type;
    question.text = this.text;
    question.image = this.image;
    question.audio = this.audio;
    question.video = this.video;
    question.index = this.index;
    question.points = this.points || 1;
    question.answers = this.answers.map(answer => answer.clone(preserveId));

    return question;
  }

  public prepareForSave(): void {
    if (DataHelper.isAutoGeneratedId(this.id)) this.id = undefined;
    this.answers.forEach(answer => answer.prepareForSave());
    this.answers.sort(ITEM_INDEX_COMPARATOR);
    updateIndexes(this.answers);
  }
}

export interface QuestionDto {
  id?: string;
  type: QuestionType;
  text?: string;
  image?: string;
  audio?: string;
  video?: string;
  index?: number;
  points?: number;
  answers: AnswerDto[];
}

export class Answer {
  public id?: string;
  public text?: string;
  public correct?: boolean;
  public index?: number;

  constructor() {
    this.id = DataHelper.generateId();
    this.correct = false;
  }

  public static parse(dto: AnswerDto): Answer {
    const answer = new Answer();

    answer.id = dto.id;
    answer.text = dto.text;
    answer.correct = dto.correct;
    answer.index = dto.index;

    return answer;
  }

  public clone(preserveId = false): Answer {
    const answer = new Answer();

    if (preserveId) answer.id = this.id;
    answer.text = this.text;
    answer.correct = this.correct;
    answer.index = this.index;

    return answer;
  }

  public prepareForSave(): void {
    if (DataHelper.isAutoGeneratedId(this.id)) this.id = undefined;
  }
}

export interface AnswerDto {
  id?: string;
  text?: string;
  correct?: boolean;
  index?: number;
}

export interface ContestTaskStats {
  section_count: number;
  section_indexes: number[];
  questions_count: Record<number, number>;
  answers_count: Record<number, Record<number, number>>;
}
